import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Box,
    Grid,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    InputAdornment,
    FormControl,
    FormHelperText,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    Paper,
    IconButton
} from "@mui/material";
import { TabPanel } from '@mui/lab';
import { Search, Clear } from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { getSkills, editSkill, getSkillRates, saveSkillRates } from '../redux/slices/skill';
import { getReportClients, getReportAccounts } from '../redux/ducks/reports';
import { getAccounts } from '../redux/ducks/account';
import { getClientFixedPriceRates, setClientFixedPriceRate } from '../redux/ducks/client';
import useSortableTable from '../utils/useSortableTable';
import { isSkillEnabled } from '../utils/skillUtils';
import ConfirmDialog from './admin/ConfirmDialog';

const RatesTabPanel = styled(TabPanel)(({ theme }) => ({
    padding: 0,
    paddingTop: theme.spacing(3),
}));

const RateList = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
}));

const RateRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: theme.spacing(1),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        alignItems: 'stretch',
        padding: theme.spacing(1.5),
    },
}));

const RateInfo = styled(Box)({
    minWidth: 0,
    flex: '1 1 auto',
});

const RateName = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    overflowWrap: 'break-word',
}));

const ScopeChip = styled(Chip)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightSemiBold,
}));

const PrecedenceNote = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const RateValueRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(0.5),
}));

const RatePill = styled(Box)(({ theme }) => ({
    display: 'inline-flex',
    alignItems: 'baseline',
    gap: theme.spacing(0.5),
    padding: theme.spacing(0.25, 1),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
    fontSize: theme.typography.size.smd,
    lineHeight: 1.4,
}));

const RatePillLabel = styled(Box)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontSize: theme.typography.size.sm,
    fontWeight: theme.typography.fontWeightSemiBold,
    textTransform: 'uppercase',
    letterSpacing: 0.4,
}));

const RatePillValue = styled(Box)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
}));

const RateRowActions = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    flexShrink: 0,
    [theme.breakpoints.down('sm')]: {
        '& > *': {
            flex: '1 1 0',
        },
    },
}));

const EmptyMessage = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    textAlign: 'center',
}));

const SearchRow = styled(Box)(({ theme }) => ({
    marginBottom: theme.spacing(2),
}));

const CenteredLoading = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
}));

const MutedCell = styled(TableCell)(({ theme }) => ({
    color: theme.palette.text.secondary,
}));

const NumberCell = styled(TableCell)({
    whiteSpace: 'nowrap',
});

const TargetPickers = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
    },
}));

const GridTable = styled(TableContainer)(({ theme }) => ({
    maxHeight: '50vh',
}));

const RateField = styled(TextField)(({ theme }) => ({
    width: theme.spacing(16),
}));

const idOf = (value) => {
    if (!value) return null;
    const raw = value._id || value.id || value;
    return raw ? raw.toString() : null;
};

const overrideRate = (value) => (Number(value) > 0 ? Number(value) : null);
const money = (value) => `$${Number(value || 0).toFixed(2)}`;
const rateKey = (rate) => `${rate.skillId}-${rate.accountId || rate.clientId || 'none'}`;
const rateScope = (rate) => (rate.accountId ? `${rate.clientName} — ${rate.accountName}` : `${rate.clientName} (all accounts)`);
const rateAmount = (value) => (value === null ? 'default' : money(value));

const BillingRates = ({ tabValue }) => {
    const dispatch = useDispatch();
    const skills = useSelector(state => state.skills || []);
    const enabledSkills = useMemo(() => skills.filter(isSkillEnabled), [skills]);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [editingSkill, setEditingSkill] = useState(null);
    const [editData, setEditData] = useState({ hourlyRate: 0, retainerRate: 0 });
    const [clients, setClients] = useState([]);
    const [accounts, setAccounts] = useState([]);
    const [allAccounts, setAllAccounts] = useState([]);
    const [search, setSearch] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const [editorOpen, setEditorOpen] = useState(false);
    const [editorClient, setEditorClient] = useState('');
    const [editorAccount, setEditorAccount] = useState('');
    const [editorRows, setEditorRows] = useState([]);
    const [editorDraft, setEditorDraft] = useState({});
    const [editorLoading, setEditorLoading] = useState(false);
    const [editorSaving, setEditorSaving] = useState(false);
    const [deletingKey, setDeletingKey] = useState(null);
    const [pendingDelete, setPendingDelete] = useState(null);

    const [fpRates, setFpRates] = useState([]);
    const [fpLoading, setFpLoading] = useState(false);
    const [fpDialogOpen, setFpDialogOpen] = useState(false);
    const [fpEditing, setFpEditing] = useState(false);
    const [fpClient, setFpClient] = useState('');
    const [fpValue, setFpValue] = useState('');
    const [fpSaving, setFpSaving] = useState(false);
    const [fpPendingRemove, setFpPendingRemove] = useState(null);
    const [fpRemovingId, setFpRemovingId] = useState(null);

    useEffect(() => {
        if (tabValue === 'rates') {
            dispatch(getSkills());
            loadClients();
            loadAllAccounts();
            loadFixedPriceRates();
        }
    }, [tabValue, dispatch]);

    const loadFixedPriceRates = () => {
        setFpLoading(true);
        dispatch(getClientFixedPriceRates((response) => {
            setFpLoading(false);
            if (response && Array.isArray(response)) {
                setFpRates(response);
            }
        }));
    };

    const loadClients = () => {
        dispatch(getReportClients((response) => {
            if (response && Array.isArray(response)) {
                setClients(response);
            }
        }));
    };

    const loadAllAccounts = () => {
        dispatch(getAccounts(0, 0, null, false, (response) => {
            if (response && Array.isArray(response)) {
                setAllAccounts(response);
            }
        }));
    };

    const loadAccounts = (clientId) => {
        if (!clientId) {
            setAccounts([]);
            return;
        }
        dispatch(getReportAccounts(clientId, (response) => {
            if (response && Array.isArray(response)) {
                setAccounts(response);
                setAllAccounts(prev => {
                    const existing = prev.filter(a => !response.some(r => idOf(r) === idOf(a)));
                    return [...existing, ...response];
                });
            }
        }));
    };

    const handleEditDefaultRate = (skill) => {
        setEditingSkill(skill);
        setEditData({
            hourlyRate: skill.hourlyRate || 0,
            retainerRate: skill.retainerRate || 0
        });
        setEditDialogOpen(true);
    };

    const handleSaveDefaultRate = () => {
        if (!editingSkill) return;
        dispatch(editSkill({
            skillId: editingSkill._id,
            skillData: {
                hourlyRate: parseFloat(editData.hourlyRate),
                retainerRate: parseFloat(editData.retainerRate)
            }
        }));
        setEditDialogOpen(false);
        setEditingSkill(null);
    };

    const clientsById = useMemo(() => new Map(clients.map(c => [idOf(c), c])), [clients]);
    const accountsById = useMemo(() => new Map(allAccounts.map(a => [idOf(a), a])), [allAccounts]);

    const specialRates = useMemo(() => {
        const rows = [];
        enabledSkills.forEach(skill => {
            (skill.accountRates || []).forEach(ar => {
                const accountId = idOf(ar.account);
                const storedClientId = idOf(ar.client);
                const account = accountId ? accountsById.get(accountId) : null;
                const resolvedClientId = storedClientId || idOf(account?.client);
                const client = resolvedClientId ? clientsById.get(resolvedClientId) : null;
                rows.push({
                    skillId: skill._id,
                    skillName: skill.name,
                    accountId,
                    clientId: resolvedClientId,
                    accountName: account?.name || (accountId ? 'Unknown account' : ''),
                    clientName: client?.name || account?.client?.name || 'Unknown client',
                    hourlyRate: overrideRate(ar.hourlyRate),
                    retainerRate: overrideRate(ar.retainerRate)
                });
            });
        });
        return rows;
    }, [enabledSkills, clientsById, accountsById]);

    const filteredRates = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return specialRates;
        return specialRates.filter(rate =>
            rate.skillName?.toLowerCase().includes(term) ||
            rate.clientName?.toLowerCase().includes(term) ||
            rate.accountName?.toLowerCase().includes(term)
        );
    }, [specialRates, search]);

    const accessors = useMemo(() => ({
        skill: (row) => row.skillName,
        client: (row) => row.clientName,
        scope: (row) => row.accountName || 'All accounts',
        hourly: (row) => row.hourlyRate,
        retainer: (row) => row.retainerRate
    }), []);

    const { sorted, order, handleSort } = useSortableTable(filteredRates, accessors, { key: 'client', dir: 'asc' });

    const sortLabelProps = (key) => ({
        active: order.key === key,
        direction: order.key === key ? order.dir : 'asc',
        onClick: () => handleSort(key)
    });

    useEffect(() => {
        setPage(0);
    }, [search, order]);

    const pageRows = useMemo(
        () => sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [sorted, page, rowsPerPage]
    );

    const editorAccountOptions = useMemo(() => {
        if (!editorAccount || accounts.some(a => idOf(a) === editorAccount)) return accounts;
        return [...accounts, accountsById.get(editorAccount) || { _id: editorAccount, name: 'Selected account' }];
    }, [accounts, editorAccount, accountsById]);

    const loadEditorRates = useCallback((clientId, accountId) => {
        if (!clientId) {
            setEditorRows([]);
            setEditorDraft({});
            return;
        }
        setEditorLoading(true);
        dispatch(getSkillRates({
            client: clientId,
            account: accountId || null,
            callback: (rates) => {
                setEditorLoading(false);
                if (!rates) return;
                const enabledRates = rates.filter((rate) => rate.enabled !== false);
                setEditorRows(enabledRates);
                const draft = {};
                enabledRates.forEach((rate) => {
                    draft[idOf(rate.skill)] = {
                        hourlyRate: rate.hourlyRate === null || rate.hourlyRate === undefined ? '' : String(rate.hourlyRate),
                        retainerRate: rate.retainerRate === null || rate.retainerRate === undefined ? '' : String(rate.retainerRate)
                    };
                });
                setEditorDraft(draft);
            }
        }));
    }, [dispatch]);

    const openEditor = (clientId = '', accountId = '') => {
        setEditorClient(clientId);
        setEditorAccount(clientId ? accountId : '');
        setEditorRows([]);
        setEditorDraft({});
        setEditorOpen(true);
        if (clientId) {
            loadAccounts(clientId);
            loadEditorRates(clientId, accountId);
        } else {
            setAccounts([]);
        }
    };

    const closeEditor = () => {
        if (editorSaving) return;
        setEditorOpen(false);
        setEditorRows([]);
        setEditorDraft({});
    };

    const handleEditorClientChange = (clientId) => {
        setEditorClient(clientId);
        setEditorAccount('');
        loadAccounts(clientId);
        loadEditorRates(clientId, null);
    };

    const handleEditorAccountChange = (accountId) => {
        setEditorAccount(accountId);
        loadEditorRates(editorClient, accountId || null);
    };

    const handleDraftChange = (skillId, field, value) => {
        setEditorDraft(prev => ({
            ...prev,
            [skillId]: { ...prev[skillId], [field]: value }
        }));
    };

    const submitRates = (clientId, accountId, rates, onDone) => {
        dispatch(saveSkillRates({
            client: clientId,
            account: accountId || null,
            rates,
            callback: (response) => {
                if (response) {
                    dispatch(getSkills());
                }
                onDone(response);
            }
        }));
    };

    const handleSaveAll = () => {
        if (!editorClient) return;
        const rates = editorRows.map((row) => {
            const skillId = idOf(row.skill);
            const draft = editorDraft[skillId] || {};
            return {
                skill: skillId,
                hourlyRate: draft.hourlyRate === '' ? null : draft.hourlyRate,
                retainerRate: draft.retainerRate === '' ? null : draft.retainerRate
            };
        });
        setEditorSaving(true);
        submitRates(editorClient, editorAccount, rates, (response) => {
            setEditorSaving(false);
            if (response) {
                setEditorOpen(false);
                setEditorRows([]);
                setEditorDraft({});
            }
        });
    };

    const handleDeleteSpecialRate = () => {
        const rate = pendingDelete;
        setPendingDelete(null);
        if (!rate || deletingKey) return;
        setDeletingKey(rateKey(rate));
        submitRates(rate.clientId, rate.accountId, [{ skill: rate.skillId, hourlyRate: null, retainerRate: null }], () => setDeletingKey(null));
    };

    const fpClientOptions = useMemo(() => {
        if (fpEditing) {
            const current = clientsById.get(fpClient) || fpRates.find(r => idOf(r) === fpClient);
            return current ? [current] : [];
        }
        const taken = new Set(fpRates.map(r => idOf(r)));
        return clients.filter(c => !taken.has(idOf(c)));
    }, [fpEditing, fpClient, clients, clientsById, fpRates]);

    const fpValueValid = fpValue !== '' && Number.isFinite(Number(fpValue)) && Number(fpValue) >= 0;

    const openFpDialog = (rate = null) => {
        setFpEditing(Boolean(rate));
        setFpClient(rate ? idOf(rate) : '');
        setFpValue(rate ? String(rate.fixedPriceRate) : '');
        setFpDialogOpen(true);
    };

    const closeFpDialog = () => {
        if (fpSaving) return;
        setFpDialogOpen(false);
    };

    const handleSaveFpRate = () => {
        if (!fpClient || !fpValueValid) return;
        setFpSaving(true);
        dispatch(setClientFixedPriceRate(fpClient, Number(fpValue), (response) => {
            setFpSaving(false);
            if (!response) return;
            setFpDialogOpen(false);
            loadFixedPriceRates();
        }));
    };

    const handleRemoveFpRate = () => {
        const rate = fpPendingRemove;
        setFpPendingRemove(null);
        if (!rate || fpRemovingId) return;
        setFpRemovingId(idOf(rate));
        dispatch(setClientFixedPriceRate(idOf(rate), null, (response) => {
            setFpRemovingId(null);
            if (response) loadFixedPriceRates();
        }));
    };

    return (
      <RatesTabPanel value="rates">
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader title="Default Rates" subheader="Configure base hourly and retainer rates for different skills" />
              <CardContent>
                <RateList>
                  {enabledSkills.length > 0 ? (
                    enabledSkills.map((skill) => (
                      <RateRow key={skill._id}>
                        <RateInfo>
                          <RateName variant="body1">{skill.name}</RateName>
                          <RateValueRow>
                            <RatePill>
                              <RatePillLabel>Hourly</RatePillLabel>
                              <RatePillValue>{money(skill.hourlyRate)}/hr</RatePillValue>
                            </RatePill>
                            <RatePill>
                              <RatePillLabel>Retainer</RatePillLabel>
                              <RatePillValue>{money(skill.retainerRate)}/hr</RatePillValue>
                            </RatePill>
                          </RateValueRow>
                        </RateInfo>
                        <RateRowActions>
                          <Button variant="outlined" size="small" onClick={() => handleEditDefaultRate(skill)}>
                            Edit
                          </Button>
                        </RateRowActions>
                      </RateRow>
                    ))
                  ) : (
                    <EmptyMessage variant="body2" color="text.secondary">
                      No skills found
                    </EmptyMessage>
                  )}
                </RateList>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card>
              <CardHeader
                title="Special Client Rates"
                subheader="Client-wide and account-specific overrides of the default skill rates"
                action={
                  <Button variant="outlined" size="small" onClick={() => openEditor()}>
                    Edit special rates
                  </Button>
                }
              />
              <CardContent>
                <PrecedenceNote variant="body2" color="text.secondary">
                  An account rate wins over a client rate, and a client rate wins over the skill's default rate.
                </PrecedenceNote>
                <SearchRow>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Search by skill, client or account"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search fontSize="small" />
                        </InputAdornment>
                      ),
                      endAdornment: search ? (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setSearch('')}>
                            <Clear fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ) : null,
                    }}
                  />
                </SearchRow>
                {sorted.length > 0 ? (
                  <>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sortDirection={order.key === 'client' ? order.dir : false}>
                              <TableSortLabel {...sortLabelProps('client')}>Client</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={order.key === 'scope' ? order.dir : false}>
                              <TableSortLabel {...sortLabelProps('scope')}>Scope</TableSortLabel>
                            </TableCell>
                            <TableCell sortDirection={order.key === 'skill' ? order.dir : false}>
                              <TableSortLabel {...sortLabelProps('skill')}>Skill</TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={order.key === 'hourly' ? order.dir : false}>
                              <TableSortLabel {...sortLabelProps('hourly')}>Hourly</TableSortLabel>
                            </TableCell>
                            <TableCell align="right" sortDirection={order.key === 'retainer' ? order.dir : false}>
                              <TableSortLabel {...sortLabelProps('retainer')}>Retainer</TableSortLabel>
                            </TableCell>
                            <TableCell align="right">Actions</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {pageRows.map((rate) => (
                            <TableRow key={rateKey(rate)} hover>
                              <TableCell>{rate.clientName}</TableCell>
                              <TableCell>
                                {rate.accountId
                                  ? rate.accountName
                                  : <ScopeChip label="All accounts" size="small" color="primary" variant="outlined" />}
                              </TableCell>
                              <TableCell>{rate.skillName}</TableCell>
                              {rate.hourlyRate === null
                                ? <MutedCell align="right">Default</MutedCell>
                                : <NumberCell align="right">{money(rate.hourlyRate)}</NumberCell>}
                              {rate.retainerRate === null
                                ? <MutedCell align="right">Default</MutedCell>
                                : <NumberCell align="right">{money(rate.retainerRate)}</NumberCell>}
                              <NumberCell align="right">
                                <RateRowActions>
                                  <Button size="small" onClick={() => openEditor(rate.clientId || '', rate.accountId || '')}>
                                    Edit
                                  </Button>
                                  <Button size="small" color="error" onClick={() => setPendingDelete(rate)} disabled={Boolean(deletingKey)}>
                                    {deletingKey === rateKey(rate) ? 'Deleting…' : 'Delete'}
                                  </Button>
                                </RateRowActions>
                              </NumberCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                    <TablePagination
                      component="div"
                      count={sorted.length}
                      page={page}
                      onPageChange={(e, value) => setPage(value)}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                      }}
                      rowsPerPageOptions={[25, 50, 100]}
                    />
                  </>
                ) : (
                  <EmptyMessage variant="body2" color="text.secondary">
                    {search ? 'No special rates match that search' : 'No special rates configured'}
                  </EmptyMessage>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={5}>
            <Card>
              <CardHeader
                title="Client Fixed-Price Rates"
                subheader="Hourly rate used to price fixed-price quotes for these clients instead of the system rate"
                action={
                  <Button variant="outlined" size="small" onClick={() => openFpDialog()}>
                    Add client rate
                  </Button>
                }
              />
              <CardContent>
                {fpLoading && fpRates.length === 0 ? (
                  <CenteredLoading>
                    <CircularProgress />
                  </CenteredLoading>
                ) : fpRates.length > 0 ? (
                  <RateList>
                    {fpRates.map((rate) => (
                      <RateRow key={idOf(rate)}>
                        <RateInfo>
                          <RateName variant="body1">{rate.name}</RateName>
                          <RateValueRow>
                            <RatePill>
                              <RatePillLabel>Fixed price</RatePillLabel>
                              <RatePillValue>{money(rate.fixedPriceRate)}/hr</RatePillValue>
                            </RatePill>
                          </RateValueRow>
                        </RateInfo>
                        <RateRowActions>
                          <Button variant="outlined" size="small" onClick={() => openFpDialog(rate)} disabled={Boolean(fpRemovingId)}>
                            Edit
                          </Button>
                          <Button size="small" color="error" onClick={() => setFpPendingRemove(rate)} disabled={Boolean(fpRemovingId)}>
                            {fpRemovingId === idOf(rate) ? 'Removing…' : 'Remove'}
                          </Button>
                        </RateRowActions>
                      </RateRow>
                    ))}
                  </RateList>
                ) : (
                  <EmptyMessage variant="body2" color="text.secondary">
                    No client fixed-price rates configured
                  </EmptyMessage>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Dialog open={fpDialogOpen} onClose={closeFpDialog} maxWidth="sm" fullWidth>
          <DialogTitle>{fpEditing ? 'Edit client fixed-price rate' : 'Add client fixed-price rate'}</DialogTitle>
          <DialogContent>
            <DialogContentText variant="body2">
              New fixed-price quotes for this client are priced at this hourly rate instead of the system rate.
            </DialogContentText>
            <FormControl fullWidth margin="normal">
              <InputLabel>Client</InputLabel>
              <Select
                value={fpClient}
                label="Client"
                onChange={(e) => setFpClient(e.target.value)}
                disabled={fpEditing || fpSaving}
              >
                {fpClientOptions.map((client) => (
                  <MenuItem key={idOf(client)} value={idOf(client)}>{client.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Fixed-price rate ($/hr)"
              type="number"
              value={fpValue}
              onChange={(e) => setFpValue(e.target.value)}
              disabled={fpSaving}
              margin="normal"
              inputProps={{ min: 0, step: 0.01 }}
              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeFpDialog} disabled={fpSaving}>Cancel</Button>
            <Button onClick={handleSaveFpRate} variant="contained" disabled={!fpClient || !fpValueValid || fpSaving}>
              {fpSaving ? 'Saving…' : 'Save rate'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Edit Default Rate</DialogTitle>
          <DialogContent>
            <DialogContentText variant="body2">
              The rate {editingSkill?.name} bills at whenever no client or account override applies.
            </DialogContentText>
            <TextField
              fullWidth
              label="Hourly Rate"
              type="number"
              value={editData.hourlyRate}
              onChange={(e) => setEditData({ ...editData, hourlyRate: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Retainer Rate"
              type="number"
              value={editData.retainerRate}
              onChange={(e) => setEditData({ ...editData, retainerRate: e.target.value })}
              margin="normal"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSaveDefaultRate} variant="contained">Save</Button>
          </DialogActions>
        </Dialog>

        <Dialog open={editorOpen} onClose={closeEditor} maxWidth="md" fullWidth>
          <DialogTitle>Special rates</DialogTitle>
          <DialogContent>
            <DialogContentText variant="body2">
              Pick a client to price every one of its accounts, or narrow it to a single account. Leave a rate
              blank to charge the rate shown in grey behind it; a rate you enter must be more than zero.
            </DialogContentText>
            <TargetPickers>
              <FormControl fullWidth>
                <InputLabel>Client</InputLabel>
                <Select
                  value={editorClient}
                  label="Client"
                  onChange={(e) => handleEditorClientChange(e.target.value)}
                  disabled={editorSaving}
                >
                  {clients.map((client) => (
                    <MenuItem key={idOf(client)} value={idOf(client)}>{client.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Account</InputLabel>
                <Select
                  value={editorAccount}
                  label="Account"
                  onChange={(e) => handleEditorAccountChange(e.target.value)}
                  disabled={!editorClient || editorSaving}
                >
                  <MenuItem value="">All accounts (client-level rate)</MenuItem>
                  {editorAccountOptions.map((account) => (
                    <MenuItem key={idOf(account)} value={idOf(account)}>{account.name}</MenuItem>
                  ))}
                </Select>
                <FormHelperText>Leave this on "All accounts" to price every account under the client the same way.</FormHelperText>
              </FormControl>
            </TargetPickers>

            {!editorClient ? (
              <EmptyMessage variant="body2" color="text.secondary">
                Pick a client to see its rates.
              </EmptyMessage>
            ) : editorLoading ? (
              <CenteredLoading>
                <CircularProgress />
              </CenteredLoading>
            ) : editorRows.length === 0 ? (
              <EmptyMessage variant="body2" color="text.secondary">
                No skills available.
              </EmptyMessage>
            ) : (
              <GridTable component={Paper} variant="outlined">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Skill</TableCell>
                      <TableCell align="right">Hourly</TableCell>
                      <TableCell align="right">Retainer</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {editorRows.map((row) => {
                      const skillId = idOf(row.skill);
                      const draft = editorDraft[skillId] || { hourlyRate: '', retainerRate: '' };
                      return (
                        <TableRow key={skillId}>
                          <TableCell>{row.name}</TableCell>
                          <NumberCell align="right">
                            <RateField
                              size="small"
                              type="number"
                              value={draft.hourlyRate}
                              placeholder={Number(row.fallbackHourlyRate || 0).toFixed(2)}
                              onChange={(e) => handleDraftChange(skillId, 'hourlyRate', e.target.value)}
                              disabled={editorSaving}
                              inputProps={{ min: 0.01, step: 0.01, 'aria-label': `${row.name} hourly rate` }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                          </NumberCell>
                          <NumberCell align="right">
                            <RateField
                              size="small"
                              type="number"
                              value={draft.retainerRate}
                              placeholder={Number(row.fallbackRetainerRate || 0).toFixed(2)}
                              onChange={(e) => handleDraftChange(skillId, 'retainerRate', e.target.value)}
                              disabled={editorSaving}
                              inputProps={{ min: 0.01, step: 0.01, 'aria-label': `${row.name} retainer rate` }}
                              InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
                            />
                          </NumberCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </GridTable>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={closeEditor} disabled={editorSaving}>Cancel</Button>
            <Button
              onClick={handleSaveAll}
              variant="contained"
              disabled={!editorClient || editorLoading || editorSaving || editorRows.length === 0}
            >
              {editorSaving ? 'Saving…' : 'Save all'}
            </Button>
          </DialogActions>
        </Dialog>

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          onClose={() => setPendingDelete(null)}
          onConfirm={handleDeleteSpecialRate}
          title="Remove special rate"
          message={pendingDelete
            ? `Remove the special ${pendingDelete.skillName} rate for ${rateScope(pendingDelete)}? It currently bills hourly ${rateAmount(pendingDelete.hourlyRate)} and retainer ${rateAmount(pendingDelete.retainerRate)}; without it that work bills at the default ${pendingDelete.skillName} rate.`
            : ''}
          confirmLabel="Remove rate"
        />

        <ConfirmDialog
          open={Boolean(fpPendingRemove)}
          onClose={() => setFpPendingRemove(null)}
          onConfirm={handleRemoveFpRate}
          title="Remove client fixed-price rate"
          message={fpPendingRemove
            ? `Remove the ${money(fpPendingRemove.fixedPriceRate)}/hr fixed-price rate for ${fpPendingRemove.name}? New fixed-price quotes for this client will use the system rate.`
            : ''}
          confirmLabel="Remove rate"
        />
      </RatesTabPanel>
    );
}

export default BillingRates;
